import clsx from 'clsx'
import { clone } from 'helpers/global.helper/global.helper'
import { useCallback, useDeferredValue, useMemo, useState } from 'react'
import { FullScreen, useFullScreenHandle } from 'react-full-screen'
import { useTranslation } from 'react-i18next'
import { Button, Inline } from 'ui/atoms'
import Checkbox from 'ui/atoms/Checkbox/Checkbox'
import KpiValue from 'ui/atoms/KpiValue/KpiValue'
import NewCheckbox from 'ui/atoms/NewCheckbox/NewCheckbox'
import SearchField from 'ui/atoms/SearchField/SearchField'
import Table, { CellValue, type ColumnGroup, cellIdToPosition, getCellId } from 'ui/atoms/Table/Table'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import { filterHiddenColumnIndices, getGroupedHeaderLayout } from './EditableDataPointTable.helper'

type EditableDataPointTableProps = {
  value: (string | null)[][]
  onSubmit?: (
    data: {
      columnIndex: number
      rowIndex: number
      value: number | null
    }[]
  ) => void
  isSubmitting?: boolean
  readOnly?: boolean
  showActionButtons?: boolean
  defaultEmptyColumnFilterChecked?: boolean
  /** Controls the empty column filter from outside; the table then hides its own toggle. */
  emptyColumnFilterChecked?: boolean
  emptyColumnFilterMode?: 'withMissingValues' | 'completelyEmpty'
  emptyColumnFilterLabel?: string
  maxHeight?: number | string
  /** Controls the column name search from outside; the table keeps its own state when omitted. */
  nameSearchTerm?: string
  /** Hides the table's search field when the parent renders one for the same term. */
  hideNameSearch?: boolean
  /** A group per column, indexed like the header row; adds a header row spanning each group's columns. */
  columnGroups?: readonly (ColumnGroup | null)[]
}

type PendingChanges = {
  [key: string]: string | null
}

const SHOW_WARNING_FOR_NR_OF_DATA_POINTS_ABOVE = 10_000
const DISABLE_SAVING_FOR_NR_OF_DATA_POINTS_ABOVE = 100_000
const COLUMN_WIDTH = 100

// Helper functions

// Group selected cells by column for processing
function getCellsByColumn(cells: Set<string>): Map<number, Set<number>> {
  const cellsByColumn: Map<number, Set<number>> = new Map()
  cells.forEach((cellId) => {
    const { columnIndex, rowIndex } = cellIdToPosition(cellId)
    if (!cellsByColumn.has(columnIndex)) {
      cellsByColumn.set(columnIndex, new Set())
    }
    cellsByColumn.get(columnIndex)?.add(rowIndex)
  })

  return cellsByColumn
}

function fillMissingValuesWithPrevious<T>(data: T[][], selectedCells: Set<string>): T[][] {
  const result = clone(data)
  const selectedCellsByColumn = getCellsByColumn(selectedCells)

  // Process each column with selected cells
  selectedCellsByColumn.forEach((rowIndices, columnIndex) => {
    let previousValue: T | null = null

    // Iterate through rows in the column
    for (let rowIndex = 1; rowIndex < data.length; rowIndex++) {
      const value = data[rowIndex]?.[columnIndex]

      if (value === null || value === undefined) {
        // Only update if the cell is selected
        if (previousValue !== null && rowIndices.has(rowIndex)) {
          result[rowIndex][columnIndex] = previousValue
        }
      } else {
        // Update the previous value when encountering a non-null value
        previousValue = value
      }
    }
  })

  return result
}

function linearInterpolateMissingValues(
  data: (string | null)[][],
  selectedCells: Set<string>,
  options?: { maxInterpolationRange?: number }
): (string | null)[][] {
  const result = clone(data)
  const maxInterpolationRange = options?.maxInterpolationRange || Infinity
  const selectedCellsByColumn = getCellsByColumn(selectedCells)

  /**
   * For each column (input data array is rows with columns in it), linearly interpolate every gap (missing data, i.e. null or undefined) up to the next valid value.
   * Assumptions:
   * - The first row is a header row and should not be interpolated.
   * - The first column is a time column and should not be interpolated.
   * - If column is completely empty, do not interpolate anything.
   * - Never overwrite an existing good value.
   */

  // Process each column with selected cells
  selectedCellsByColumn.forEach((rowIndices, columnIndex) => {
    let previousValue: number | null = null
    let previousRowIndex: number | null = null

    for (let rowIndex = 1; rowIndex < data.length; rowIndex++) {
      const value = data[rowIndex]?.[columnIndex]

      if (value !== null && value !== undefined && !isNaN(Number(value))) {
        previousRowIndex = rowIndex
        previousValue = Number(value)
        continue
      }

      if (previousValue === null) {
        continue
      }

      // Find the next non-null value
      let nextValue: number | null = null
      let nextRowIndex: number | null = null
      for (let nextRow = rowIndex + 1; nextRow < data.length; nextRow++) {
        const nextValueCandidate = data[nextRow]?.[columnIndex]
        if (nextValueCandidate !== null && nextValueCandidate !== undefined && !isNaN(Number(nextValueCandidate))) {
          nextValue = Number(nextValueCandidate)
          nextRowIndex = nextRow
          break
        }
      }

      if (nextValue === null || nextRowIndex === null || previousRowIndex === null || previousValue === null) {
        break
      }

      const range = nextRowIndex - previousRowIndex
      if (range - 1 > maxInterpolationRange) {
        continue
      }

      const valueDiff = nextValue - previousValue
      const step = valueDiff / range
      for (let r = previousRowIndex + 1; r < nextRowIndex; r++) {
        // Only update if the cell is selected
        if (rowIndices.has(r)) {
          const interpolatedValue = previousValue + step * (r - previousRowIndex)
          const resValue = Math.round(interpolatedValue * 1000) / 1000 // Round to 3 decimal places
          result[r][columnIndex] = `${resValue}`
        }
      }

      rowIndex = nextRowIndex - 1 // Skip ahead to the next non-null value
    }
  })

  return result
}

function getDiffValues<T>(originalData: T[][], newData: T[][]): T[][] {
  // Check if arrays have the same shape
  if (originalData.length !== newData.length) {
    throw new Error('Original and new data have different number of rows')
  }

  // Create a new array with the same shape as newData
  const result: (T | undefined)[][] = []

  // Compare each cell and only keep values that are different
  for (let rowIndex = 0; rowIndex < newData.length; rowIndex++) {
    const originalRow = originalData[rowIndex] || []
    const newRow = newData[rowIndex] || []

    if (originalRow.length !== newRow.length) {
      throw new Error(`Row ${rowIndex} has different number of columns in original and new data`)
    }

    result[rowIndex] = []
    for (let columnIndex = 0; columnIndex < newRow.length; columnIndex++) {
      const originalValue = originalRow[columnIndex]
      const newValue = newRow[columnIndex]

      // Only keep values that differ between original and new data
      result[rowIndex][columnIndex] = originalValue === newValue ? undefined : newValue
    }
  }

  return result as T[][]
}

function arrayArrayToCellIdMap<T>(data: T[][]): Record<string, T> {
  const map: Record<string, T> = {}

  data.forEach((row, rowIndex) => {
    row.forEach((value, columnIndex) => {
      if (value === undefined) {
        return
      }

      const cellId = getCellId(rowIndex, columnIndex)
      map[cellId] = value
    })
  })

  return map
}

function cellValueToNumberOrNull(value: number | string | null): number | null {
  if (value === null || value === '') {
    return null
  }

  if (typeof value === 'number') {
    if (isNaN(value)) {
      return null
    }

    return value
  }

  if (typeof value === 'string') {
    const parsedValue = parseFloat(value.replace(',', '.'))

    if (isNaN(parsedValue)) {
      return null
    }

    return parsedValue
  }

  return null
}

export default function EditableDataPointTable({
  value: initialValue,
  onSubmit,
  isSubmitting,
  readOnly = false,
  showActionButtons = true,
  defaultEmptyColumnFilterChecked = false,
  emptyColumnFilterChecked: controlledEmptyColumnFilterChecked,
  emptyColumnFilterMode = 'withMissingValues',
  emptyColumnFilterLabel,
  maxHeight,
  nameSearchTerm: controlledNameSearchTerm,
  hideNameSearch = false,
  columnGroups,
}: EditableDataPointTableProps): React.JSX.Element {
  const [currentData, setCurrentData] = useState<CellValue[][]>([])
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({})
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set())
  const [localNameSearchTerm, setLocalNameSearchTerm] = useState('')
  const nameSearchTerm = controlledNameSearchTerm ?? localNameSearchTerm
  const setNameSearchTerm = setLocalNameSearchTerm
  const [localEmptyColumnFilterChecked, setLocalEmptyColumnFilterChecked] = useState(defaultEmptyColumnFilterChecked)
  const showOnlyColumnsWithMissingValues = controlledEmptyColumnFilterChecked ?? localEmptyColumnFilterChecked
  const showEmptyColumnToggle = controlledEmptyColumnFilterChecked === undefined
  const deferredNameSearchTerm = useDeferredValue(nameSearchTerm)
  const { t, i18n } = useTranslation()
  const shouldShowActionButtons = showActionButtons && !readOnly
  const resolvedEmptyColumnToggleLabel =
    emptyColumnFilterLabel ||
    (emptyColumnFilterMode === 'completelyEmpty' ? t('Hide empty columns') : t('Show only columns with missing values'))

  const hiddenColumnIndices = useMemo(() => {
    return filterHiddenColumnIndices(
      initialValue,
      deferredNameSearchTerm,
      showOnlyColumnsWithMissingValues,
      emptyColumnFilterMode,
      columnGroups
    )
  }, [initialValue, deferredNameSearchTerm, showOnlyColumnsWithMissingValues, emptyColumnFilterMode, columnGroups])

  const selectedColumns = useMemo(
    () =>
      Array.from(selectedCells).reduce((columns, cellId) => {
        const { columnIndex } = cellIdToPosition(cellId)
        if (!columns.includes(columnIndex)) {
          columns.push(columnIndex)
        }
        return columns
      }, [] as number[]),
    [selectedCells]
  )

  const selectedColumnsHaveMissingValues = useMemo(() => {
    return selectedColumns.some((columnIndex) => {
      return currentData.some((row, rowIndex) => {
        // Skip the header row (rowIndex === 0)
        if (rowIndex === 0) return false
        const value = row[columnIndex]
        return value === null || value === '' || value === undefined
      })
    })
  }, [selectedColumns, currentData])

  const pendingCellValues = useMemo(() => {
    return Object.entries(pendingChanges).map(([id, value]) => {
      const { columnIndex, rowIndex } = cellIdToPosition(id)

      return {
        columnIndex,
        rowIndex,
        value,
      }
    })
  }, [pendingChanges])

  const applyPendingChanges = useCallback(
    ({ type, changes }: { type: 'set' | 'override' | 'reset'; changes?: PendingChanges }) => {
      if (!changes) {
        changes = {}
      }

      if (type === 'set') {
        setPendingChanges((prev) => ({ ...prev, ...changes }))
      } else if (type === 'override') {
        setPendingChanges(changes)
      } else if (type === 'reset') {
        setPendingChanges({})
      }
    },
    []
  )

  // Stats
  const { nrOfMissingValues, nrOfTotalDataPoints } = useMemo(() => {
    let nrOfMissingValues = 0
    const nrOfTotalDataPoints = (currentData.length - 1) * (currentData[0]?.length - 1)

    currentData.forEach((row) => {
      row.forEach((value) => {
        if (value === null || value === '' || value === undefined) {
          nrOfMissingValues++
        }
      })
    })
    return { nrOfMissingValues, nrOfTotalDataPoints }
  }, [currentData])
  const nrOfPendingChanges = useMemo(() => {
    return Object.keys(pendingChanges).length
  }, [pendingChanges])

  const { hasChanges, hasSelection } = useMemo(() => {
    return {
      hasChanges: nrOfPendingChanges > 0,
      hasSelection: selectedCells.size > 0,
    }
  }, [nrOfPendingChanges, selectedCells])

  // Commit changes with stable callback
  const commitChanges = useCallback(() => {
    const changedData = Object.entries(pendingChanges).map(([key, value]) => {
      const { rowIndex, columnIndex } = cellIdToPosition(key)

      return {
        columnIndex: columnIndex,
        rowIndex: rowIndex,
        value: cellValueToNumberOrNull(value),
      }
    })

    if (onSubmit !== undefined) {
      onSubmit(changedData)
    }
  }, [pendingChanges, onSubmit])

  // Helper operations for selected cells
  const setSelectedToNull = useCallback(() => {
    const newChanges: PendingChanges = {}
    selectedCells.forEach((cellId) => {
      newChanges[cellId] = null
    })

    // Batch update all changes at once
    applyPendingChanges({ type: 'set', changes: newChanges })
  }, [selectedCells, applyPendingChanges])

  const resetSelectionToOriginalValue = useCallback(() => {
    if (!hasChanges) {
      return
    }

    // Reset pending changes completely for selected cells
    const newChanges: PendingChanges = clone(pendingChanges)
    selectedCells.forEach((cellId) => {
      delete newChanges[cellId]
    })

    // Batch update all changes at once
    applyPendingChanges({ type: 'override', changes: newChanges })
  }, [applyPendingChanges, hasChanges, pendingChanges, selectedCells])

  const onCellSelection = useCallback((selectedCellsAsArray: { columnIndex: number; rowIndex: number }[]) => {
    const newSelection = new Set(
      selectedCellsAsArray.map(({ columnIndex, rowIndex }) => {
        return getCellId(rowIndex, columnIndex)
      })
    )

    setSelectedCells(newSelection)
  }, [])

  const onPendingChange = useCallback((newPendingChanges: PendingChanges) => {
    setPendingChanges(newPendingChanges)
  }, [])

  const fullScreenHandle = useFullScreenHandle()
  const { active: isFullScreen, enter, exit } = fullScreenHandle

  const saveButtonDisabledTooltip = useMemo(() => {
    if (nrOfPendingChanges > DISABLE_SAVING_FOR_NR_OF_DATA_POINTS_ABOVE) {
      return t('Too many pending changes')
    }
    if (isSubmitting) {
      return `${t('Saving')}...`
    }
    if (!hasChanges) {
      return t('No changes to save')
    }
  }, [hasChanges, isSubmitting, nrOfPendingChanges, t])

  const groupedHeaderLayout = columnGroups ? getGroupedHeaderLayout(initialValue[0] ?? [], COLUMN_WIDTH) : undefined

  return (
    <div className="h-full w-full min-w-0" style={maxHeight === undefined ? undefined : { maxHeight }}>
      <FullScreen handle={fullScreenHandle} className="flex flex-col h-full w-full min-w-0 bg-white">
        <div className="flex flex-row justify-between gap-x-5 my-2 items-center">
          {!readOnly && (
            <div className="flex flex-row gap-x-5">
              <KpiValue value={nrOfTotalDataPoints} label={t('Total Data Points')} decimals={0} />
              <KpiValue value={nrOfMissingValues} label={t('Missing values')} decimals={0} />
              <KpiValue value={nrOfPendingChanges} label={t('Pending changes')} decimals={0} />
              <KpiValue value={selectedCells.size} label={t('Selected cells')} decimals={0} />
            </div>
          )}

          {readOnly ? (
            <div className="flex flex-row justify-end w-full gap-x-3 items-center px-4">
              {showEmptyColumnToggle && (
                <NewCheckbox
                  label={resolvedEmptyColumnToggleLabel}
                  checked={showOnlyColumnsWithMissingValues}
                  onChange={() => setLocalEmptyColumnFilterChecked((prev) => !prev)}
                />
              )}
              <div className="ml-auto flex items-center gap-x-3">
                {!hideNameSearch && (
                  <div className="w-[220px]">
                    <SearchField
                      value={nameSearchTerm}
                      onChange={(newNameSearchTerm) => setNameSearchTerm(String(newNameSearchTerm))}
                      onClear={() => setNameSearchTerm('')}
                    />
                  </div>
                )}
              </div>
              <TooltipIconButton
                tooltip={isFullScreen ? t(`Minimize fullscreen`) : t(`Fullscreen`)}
                onClick={() => {
                  if (isFullScreen) {
                    exit()
                  } else {
                    enter()
                  }
                }}
                icon={isFullScreen ? `fal fa-compress-wide` : `fal fa-expand`}
              />
            </div>
          ) : (
            <div className="w-[300px] flex flex-row justify-end gap-x-3">
              <div className="flex flex-col justify-start">
                {!readOnly && (
                  <SearchField
                    value={nameSearchTerm}
                    onChange={(newNameSearchTerm) => setNameSearchTerm(String(newNameSearchTerm))}
                    onClear={() => setNameSearchTerm('')}
                  />
                )}
                {showEmptyColumnToggle && (
                  <Checkbox
                    label={resolvedEmptyColumnToggleLabel}
                    isChecked={showOnlyColumnsWithMissingValues}
                    onClick={() => setLocalEmptyColumnFilterChecked((prev) => !prev)}
                  />
                )}
              </div>
              <TooltipIconButton
                className="ml-auto"
                tooltip={isFullScreen ? t(`Minimize fullscreen`) : t(`Fullscreen`)}
                onClick={() => {
                  if (isFullScreen) {
                    exit()
                  } else {
                    enter()
                  }
                }}
                icon={isFullScreen ? `fal fa-compress-wide` : `fal fa-expand`}
              />
            </div>
          )}
        </div>

        {shouldShowActionButtons &&
          nrOfPendingChanges > SHOW_WARNING_FOR_NR_OF_DATA_POINTS_ABOVE &&
          nrOfPendingChanges < DISABLE_SAVING_FOR_NR_OF_DATA_POINTS_ABOVE && (
            <InfoBanner
              style="warning"
              text={t(
                `Warning, you have a large amount of pending changes. Are you sure you want to save {{count}} data points?`,
                { count: nrOfMissingValues }
              )}
            />
          )}

        {shouldShowActionButtons && nrOfPendingChanges > DISABLE_SAVING_FOR_NR_OF_DATA_POINTS_ABOVE && (
          <InfoBanner
            style="danger"
            text={t(`Saving has been disabled since you have more than {{count}} pending changes.`, {
              count: DISABLE_SAVING_FOR_NR_OF_DATA_POINTS_ABOVE,
            })}
          />
        )}

        <Table
          value={initialValue}
          pendingCellValues={pendingCellValues}
          hiddenColumnIndices={hiddenColumnIndices}
          onChange={setCurrentData}
          onSubmit={commitChanges}
          onCellSelection={onCellSelection}
          onPendingChange={onPendingChange}
          readOnly={readOnly}
          firstColumnWidth={140}
          columnWidth={COLUMN_WIDTH}
          firstRowHeight={groupedHeaderLayout?.rowHeight ?? 70}
          columnGroups={columnGroups}
          HeaderComponent={({ value }) => {
            if (typeof value !== 'string') {
              return <span>{value}</span>
            }

            const [title, subTitle = ''] = value.split('\n')

            return (
              <div
                className={clsx(
                  'flex flex-col items-center space-y-2 h-full w-full max-w-full px-1 pb-1 overflow-hidden border-b-1',
                  groupedHeaderLayout && 'justify-center'
                )}
              >
                <Tooltip title={title} arrow placement="top" display="block">
                  <div
                    className={clsx(
                      'text-center w-full overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-box-orient:vertical]',
                      groupedHeaderLayout
                        ? 'text-xs font-semibold hyphens-auto break-words'
                        : 'font-bold [-webkit-line-clamp:2]'
                    )}
                    style={groupedHeaderLayout && { WebkitLineClamp: groupedHeaderLayout.titleLineCount }}
                    lang={i18n.language}
                  >
                    {title}
                  </div>
                </Tooltip>
                <div className="text-xs text-stone-500 wrap-anywhere text-center">{subTitle}</div>
              </div>
            )
          }}
        />

        {shouldShowActionButtons && (
          <div className="bg-background sticky right-0 bottom-0 left-0 z-20 px-3">
            <Inline spaceBetween fullWidth marginTop marginBottom>
              <Inline>
                <Button
                  primary
                  marginRight
                  onClick={() => {
                    const newData = fillMissingValuesWithPrevious(currentData, selectedCells)
                    const diffData = getDiffValues(currentData, newData)
                    const newPendingChanges = arrayArrayToCellIdMap(diffData)

                    applyPendingChanges({
                      type: 'set',
                      changes: newPendingChanges,
                    })
                  }}
                  disabled={!hasSelection || !selectedColumnsHaveMissingValues}
                >
                  {t('Interpolate with previous value')}
                </Button>
                <Button
                  primary
                  onClick={() => {
                    const newData = linearInterpolateMissingValues(currentData, selectedCells, {
                      maxInterpolationRange: Infinity,
                    })
                    const diffData = getDiffValues(currentData, newData)
                    const newPendingChanges = arrayArrayToCellIdMap(diffData)

                    applyPendingChanges({
                      type: 'set',
                      changes: newPendingChanges,
                    })
                  }}
                  disabled={!hasSelection || !selectedColumnsHaveMissingValues}
                >
                  {t('Linear interpolation')}
                </Button>
              </Inline>

              <Inline flexEnd>
                <Button marginRight danger secondary onClick={setSelectedToNull} disabled={!hasSelection}>
                  {t('Set to null')}
                </Button>

                <Button marginRight secondary onClick={resetSelectionToOriginalValue} disabled={!hasChanges}>
                  {t('Reset to original')}
                </Button>

                <Button
                  primary
                  onClick={commitChanges}
                  disabled={
                    !hasChanges || nrOfPendingChanges > DISABLE_SAVING_FOR_NR_OF_DATA_POINTS_ABOVE || isSubmitting
                  }
                  disabledTooltip={saveButtonDisabledTooltip}
                >
                  {t('Save changes')}
                </Button>
              </Inline>
            </Inline>
          </div>
        )}
      </FullScreen>
    </div>
  )
}
